import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SuccessorPath } from "@/components/succession/SuccessorPath";

interface SuccessorContext {
  successor_id: string;
  association_id: string;
  association_name: string;
  officer_role_to_succeed: string | null;
  successor_name: string | null;
  successor_status: string;
}

interface PendingInvite {
  association_name: string | null;
  officer_role_to_succeed: string | null;
  successor_name: string | null;
}

export default function SuccessorLanding() {
  const { user, signOut } = useAuth();
  const [ctx, setCtx] = useState<SuccessorContext | null>(null);
  const [pending, setPending] = useState<PendingInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  async function loadContext() {
    const { data } = await supabase.rpc("get_my_successor_context");
    const row = Array.isArray(data) ? data[0] : (data as any);
    if (row) setCtx(row as SuccessorContext);
    return row as SuccessorContext | null;
  }

  async function loadPendingInvite() {
    if (!user?.email) return null;
    const { data, error } = await supabase.rpc("get_my_pending_successor_invitation");
    if (error) return null;
    const row = Array.isArray(data) ? data[0] : (data as any);
    if (!row) return null;
    return {
      association_name: row.association_name ?? null,
      officer_role_to_succeed: row.officer_role_to_succeed ?? null,
      successor_name: row.successor_name ?? null,
    } as PendingInvite;
  }

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const existing = await loadContext();
      if (!existing) {
        const p = await loadPendingInvite();
        setPending(p);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function acceptInvitation() {
    setClaiming(true);
    try {
      const { error } = await supabase.rpc("claim_successor_invitation");
      if (error) throw error;
      await loadContext();
      setPending(null);
      toast.success("Invitation accepted.");
    } catch (e: any) {
      toast.error(e?.message || "Could not accept the invitation.");
    } finally {
      setClaiming(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Already claimed: show the path.
  if (ctx) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Sparkles className="h-6 w-6 text-primary" />
            <Button variant="ghost" size="sm" onClick={signOut}>Sign out</Button>
          </div>
          <SuccessorPath
            successorId={ctx.successor_id}
            successorName={ctx.successor_name}
            associationName={ctx.association_name}
            officerRole={ctx.officer_role_to_succeed}
          />
        </div>
      </div>
    );
  }

  // Pending invitation waiting to be accepted
  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <Card className="max-w-xl w-full p-10 space-y-6 text-center">
          <Sparkles className="h-10 w-10 mx-auto text-primary" />
          <h1 className="text-3xl font-serif">You have an invitation.</h1>
          <div className="space-y-2 text-muted-foreground">
            <p className="text-lg">
              {pending.association_name || "An Association"} has invited you to get ready as{" "}
              <span className="font-medium text-foreground">
                {pending.officer_role_to_succeed || "a steward"}
              </span>.
            </p>
            <p className="text-sm">
              Saying yes adds the successor role to your account. Any other roles you already have stay the same.
            </p>
          </div>
          <Button size="lg" onClick={acceptInvitation} disabled={claiming}>
            {claiming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Say yes to your invitation
          </Button>
          <Button variant="ghost" onClick={signOut}>Sign out</Button>
        </Card>
      </div>
    );
  }

  // No invitation and no successor row
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <Card className="max-w-xl w-full p-10 space-y-6 text-center">
        <h1 className="text-2xl font-serif">No invitation found.</h1>
        <p className="text-muted-foreground">
          We could not find an invitation for your email. If you were expecting one, ask the person who invited you to send it again.
        </p>
        <Button variant="ghost" onClick={signOut}>Sign out</Button>
      </Card>
    </div>
  );
}
